import { useSpecimen } from '../../services/httpServices/queries';
import type { LabSpecimen } from '../../services/httpServices/diagnosticService';
import { specimenCategoryNames } from '../../utils/specimen';
import { joinFullName } from '../../utils/patientName';
import { Badge } from './badge';
import { Button } from './button';
import { Loader2, AlertCircle, X, FlaskConical, User, Barcode, Building2, Clock, ExternalLink, Tag } from 'lucide-react';

const statusMeta: Record<
  string,
  { label: string; variant: 'warning' | 'secondary' | 'primary' | 'success' | 'danger' | 'default' }
> = {
  PENDING: { label: 'Pending', variant: 'warning' },
  COLLECTED: { label: 'Collected', variant: 'primary' },
  RECEIVED: { label: 'Received', variant: 'secondary' },
  PROCESSING: { label: 'Processing', variant: 'primary' },
  COMPLETED: { label: 'Completed', variant: 'success' },
  REJECTED: { label: 'Rejected', variant: 'danger' },
  CANCELLED: { label: 'Cancelled', variant: 'danger' },
};

function formatDateTime(value?: string): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
}

function testsLabel(specimen: LabSpecimen): string {
  return (specimen.items || [])
    .map((i) => i.receiptItem?.testName)
    .filter(Boolean)
    .join(', ');
}

interface SpecimenDetailsModalProps {
  /** When set, the modal fetches and shows the specimen with this id. */
  specimenId: string | null;
  onClose: () => void;
  /** Optional handler shown as a "View Receipt" action when the specimen has a receipt. */
  onViewReceipt?: (receiptId: string) => void;
}

/**
 * Read-only detail view for a lab specimen, shared by the collection and lab
 * queues so a row's specimen can be inspected without leaving the list.
 */
export function SpecimenDetailsModal({ specimenId, onClose, onViewReceipt }: SpecimenDetailsModalProps) {
  const { data, isLoading, error } = useSpecimen(specimenId || undefined);
  const specimen = data?.data;

  if (!specimenId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-surface z-10 flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <FlaskConical className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-text">Specimen Details</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error || !specimen ? (
          <div className="flex items-center gap-2 p-4 m-5 bg-danger/10 border border-danger/20 rounded-lg text-danger">
            <AlertCircle size={18} />
            <span>Failed to load specimen details. Please try again.</span>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <User size={16} className="text-primary" />
                  <span className="text-base font-semibold text-text">
                    {joinFullName(specimen.patient || {})}
                  </span>
                </div>
                <p className="text-xs text-text-muted">
                  {[specimen.patient?.age ? `${specimen.patient.age} yrs` : null, specimen.patient?.gender]
                    .filter(Boolean)
                    .join(' · ') || 'Patient details unavailable'}
                  {specimen.patient?.mobileNumber || specimen.patient?.mobile
                    ? ` · ${specimen.patient.mobileNumber || specimen.patient.mobile}`
                    : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {specimen.isUrgent && <Badge variant="danger">URGENT</Badge>}
                <Badge variant={statusMeta[specimen.status]?.variant || 'default'}>
                  {statusMeta[specimen.status]?.label || specimen.status}
                </Badge>
              </div>
            </div>

            {/* Core details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Detail icon={<Barcode size={14} />} label="Barcode" valueClassName="font-mono text-primary">
                {specimen.specimenBarcode}
              </Detail>
              <Detail icon={<FlaskConical size={14} />} label="Container">
                {specimen.specimenContainerCode
                  ? `${specimen.specimenContainerCode}${specimen.containerColor ? ` · ${specimen.containerColor}` : ''}`
                  : '—'}
              </Detail>
              <Detail icon={<Tag size={14} />} label="Category">
                {specimenCategoryNames(specimen) || '—'}
              </Detail>
              <Detail icon={<Building2 size={14} />} label="Room">
                {specimen.room?.name || '—'}
              </Detail>
              <Detail icon={<FlaskConical size={14} />} label="Specimen Type">
                {specimen.specimenType?.name || specimen.specimenTypeCode || '—'}
              </Detail>
              <Detail icon={<ExternalLink size={14} />} label="Receipt">
                {specimen.receipt?.receiptNumber || '—'}
              </Detail>
            </div>

            {/* Tests */}
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-text-muted uppercase">Tests</p>
              {testsLabel(specimen) ? (
                <div className="flex flex-wrap gap-1.5">
                  {(specimen.items || [])
                    .map((i) => i.receiptItem?.testName)
                    .filter(Boolean)
                    .map((name, idx) => (
                      <Badge key={`${name}-${idx}`} variant="default">
                        {name}
                      </Badge>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-text-muted">No linked tests.</p>
              )}
            </div>

            {/* Timeline */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-text-muted uppercase flex items-center gap-1.5">
                <Clock size={12} /> Timeline
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <Detail label="Created">{formatDateTime(specimen.createdAt)}</Detail>
                <Detail label="Collected">{formatDateTime(specimen.collectedAt)}</Detail>
                <Detail label="Received">{formatDateTime(specimen.receivedAt)}</Detail>
                <Detail label="Completed">{formatDateTime(specimen.completedAt)}</Detail>
                <Detail label="Label Printed">{formatDateTime(specimen.labelPrintedAt)}</Detail>
                <Detail label="Print Count">{specimen.printCount ?? 0}</Detail>
              </div>
            </div>

            {specimen.status === 'REJECTED' && (
              <div className="p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm">
                <p className="font-medium text-danger">Rejection reason</p>
                <p className="text-text-muted mt-0.5">{specimen.rejectionReason || 'No reason recorded'}</p>
                {specimen.rejectedAt && (
                  <p className="text-xs text-text-muted mt-1">Rejected {formatDateTime(specimen.rejectedAt)}</p>
                )}
              </div>
            )}

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
              {onViewReceipt && specimen.receiptId && (
                <Button variant="outline" size="sm" onClick={() => onViewReceipt(specimen.receiptId)}>
                  <ExternalLink size={14} className="mr-1" /> View Receipt
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Detail({
  icon,
  label,
  valueClassName,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  valueClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-background rounded-lg p-2.5 border border-border">
      <p className="text-[0.6875rem] text-text-muted flex items-center gap-1">
        {icon}
        {label}
      </p>
      <p className={`text-sm font-medium mt-0.5 break-words ${valueClassName || 'text-text'}`}>{children}</p>
    </div>
  );
}
